import { FormInputGroup, Input } from '@aqarmap/aqarmap-ui';
import { useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  AddListingDescriptionType,
  descriptionFieldConfigType,
} from '~/modules/addListing/types';
import Lottie from 'lottie-react';
import moreAnimationData from '~/animations/more.json';
import AutoGenerateButton from '~/modules/addListing/pages/ListingDescription/AutoGenerateButton';
import { useBreakpoint } from '~/hooks';
import {
  HandleGenerateValueByAIType,
  KeysGeneratedByAIType,
} from '~addListing/pages/ListingDescription/types';
import FormLabel from '~addListing/pages/ListingDescription/InputLabel';

const descriptionFieldConfig: descriptionFieldConfigType = {
  arabic: {
    title: {
      key: 'title-ar',
      errorMessage: 'listingTitleError',
    },
    description: {
      key: 'description-ar',
      errorMessage: 'listingDescriptionError',
    },
    address: {
      key: 'address-ar',
      errorMessage: 'listingAddressError',
    },
  },
  english: {
    title: {
      key: 'title-en',
      errorMessage: 'listingTitleError',
    },
    description: {
      key: 'description-en',
      errorMessage: 'listingDescriptionError',
    },
    address: {
      key: 'address-en',
      errorMessage: 'listingAddressError',
    },
  },
};

const DescriptionForm = ({
  language = 'arabic',
  handleGenerateValueByAI,
  keysGeneratedByAI,
}: {
  language?: 'arabic' | 'english';
  handleGenerateValueByAI: HandleGenerateValueByAIType;
  keysGeneratedByAI: KeysGeneratedByAIType;
}) => {
  const { t } = useTranslation('app', {
    lng: language === 'arabic' ? 'ar' : 'en',
  });
  const { mobileScreen } = useBreakpoint();

  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const config = descriptionFieldConfig[language];

  const {
    setValue,
    watch,
    formState: { errors },
    clearErrors,
  } = useFormContext<AddListingDescriptionType>();

  const handleChangeDescription = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setValue(config.description.key, e.target.value);
  };

  return (
    <div
      className="flex flex-col gap-4x md:gap-5x text-gray__dark_1"
      dir={language === 'arabic' ? 'rtl' : 'ltr'}
    >
      <FormInputGroup
        error={
          errors[config.title.key]
            ? `${
                errors[config.title.key]?.message === 'Required'
                  ? t(config.title.errorMessage)
                  : t(`${errors[config.title.key]?.message}`)
              }`
            : ''
        }
        helperText={t('listingNameLabel')}
        hasBorder={true}
        endIcon={
          !mobileScreen && (
            <AutoGenerateButton
              isLoading={
                keysGeneratedByAI.find((val) => config.title.key in val)?.[
                  config.title.key
                ]?.isLoading ?? false
              }
              isSuccess={
                keysGeneratedByAI.find((val) => config.title.key in val)?.[
                  config.title.key
                ]?.isSuccess ?? false
              }
              handleGenerateValueByAI={() =>
                handleGenerateValueByAI(config.title.key, titleRef)
              }
              language={language}
            />
          )
        }
        label={{
          component: (
            <FormLabel
              title={t('listingName')}
              isLoading={
                keysGeneratedByAI.find((val) => config.title.key in val)?.[
                  config.title.key
                ]?.isLoading ?? false
              }
              isSuccess={
                keysGeneratedByAI.find((val) => config.title.key in val)?.[
                  config.title.key
                ]?.isSuccess ?? false
              }
              handleGenerateValueByAI={() =>
                handleGenerateValueByAI(config.title.key, titleRef)
              }
              language={language}
            />
          ),
        }}
      >
        <div className=" flex items-center  gap-2x px-1.5x py-x w-full">
          {keysGeneratedByAI.find((val) => config.title.key in val)?.[
            config.title.key
          ]?.isFetching ? (
            <div className="flex-1">
              <Lottie
                animationData={moreAnimationData}
                loop={
                  keysGeneratedByAI.find((val) => config.title.key in val)?.[
                    config.title.key
                  ]?.isLoading ?? false
                }
                className="w-[24px] h-[24px]"
              />
            </div>
          ) : (
            <textarea
              data-clarity-unmask
              ref={titleRef}
              placeholder={t('listingNamePlaceholder')}
              value={watch(config.title.key)}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setValue(config.title.key, e.target.value)
              }
              onFocus={() => clearErrors(config.title.key)}
              rows={1}
              className={`flex-1 resize-none overflow-hidden focus:border-0 focus:outline-none overflow-y-auto focus:outline-0 placeholder:text-gray__dark_1 ${
                watch(config.title.key)
                  ? 'text-gray__dark_2'
                  : 'text-gray__dark_1'
              }`}
            />
          )}
        </div>
      </FormInputGroup>

      <FormInputGroup
        error={
          errors[config.description.key]
            ? errors[config.description.key]?.message === 'Required'
              ? t(config.description.errorMessage)
              : t(`${errors[config.description.key]?.message}`)
            : ''
        }
        hasBorder={true}
        label={{
          component: (
            <FormLabel
              title={t('listingDescription')}
              isLoading={
                keysGeneratedByAI.find(
                  (val) => config.description.key in val,
                )?.[config.description.key]?.isLoading ?? false
              }
              isSuccess={
                keysGeneratedByAI.find(
                  (val) => config.description.key in val,
                )?.[config.description.key]?.isSuccess ?? false
              }
              handleGenerateValueByAI={() =>
                handleGenerateValueByAI(config.description.key, descriptionRef)
              }
              language={language}
            />
          ),
        }}
      >
        <div className="flex items-center px-1.5x py-x relative gap-2x w-full min-h-[204px]">
          {keysGeneratedByAI.find((val) => config.description.key in val)?.[
            config.description.key
          ]?.isFetching ? (
            <div className="flex-1 self-start">
              <Lottie
                animationData={moreAnimationData}
                loop={
                  keysGeneratedByAI.find(
                    (val) => config.description.key in val,
                  )?.[config.description.key]?.isLoading ?? false
                }
                className="w-[24px] h-[24px]"
              />
            </div>
          ) : (
            <textarea
              data-clarity-unmask
              ref={descriptionRef}
              placeholder={t('listingDetailsPlaceholder')}
              value={watch(config.description.key)}
              onChange={handleChangeDescription}
              rows={8}
              onFocus={() => clearErrors(config.description.key)}
              className={`flex-1 md:pe-[161px] resize-none overflow-hidden focus:outline-none self-start focus:border-0 overflow-y-auto focus:outline-0 placeholder:text-gray__dark_1 ${
                watch(config.description.key)
                  ? 'text-gray__dark_2'
                  : 'text-gray__dark_1'
              }`}
            />
          )}
          {!mobileScreen && (
            <AutoGenerateButton
              isLoading={
                keysGeneratedByAI.find(
                  (val) => config.description.key in val,
                )?.[config.description.key]?.isLoading ?? false
              }
              isSuccess={
                keysGeneratedByAI.find(
                  (val) => config.description.key in val,
                )?.[config.description.key]?.isSuccess ?? false
              }
              handleGenerateValueByAI={() =>
                handleGenerateValueByAI(config.description.key, descriptionRef)
              }
              language={language}
              className="absolute top-2 end-2"
            />
          )}
        </div>
      </FormInputGroup>

      <div className="flex flex-col gap-1.5x">
        <Input
          field={{
            type: 'text',
            placeholder: t('listingAddressPlaceholder'),
            defaultValue: watch(config.address.key),
            onChange: (e) => setValue(config.address.key, e.target.value),
            onFocus: () => clearErrors(config.address.key),
            className: `${
              watch(config.address.key)
                ? 'text-gray__dark_2'
                : 'text-gray__dark_1'
            }`,
          }}
          label={{
            text: t('listingAddress'),
            className: 'text-title_4 text-black',
          }}
          error={
            errors[config.address.key]
              ? `${
                  errors[config.address.key]?.message === 'Required'
                    ? t(config.address.errorMessage)
                    : t(`${errors[config.address.key]?.message}`)
                }`
              : ''
          }
        />
      </div>
    </div>
  );
};

export default DescriptionForm;
